import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Plus, 
  Search, 
  Filter,
  Clock,
  User,
  AlertCircle,
  CheckCircle,
  Loader2,
  Mail,
  Phone,
  Calendar,
  FileText,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500/20 text-blue-400 border-blue-500/50' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' },
  waiting: { label: 'Waiting', color: 'bg-purple-500/20 text-purple-400 border-purple-500/50' },
  closed: { label: 'Closed', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/50' }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-zinc-500/20 text-zinc-400' },
  medium: { label: 'Medium', color: 'bg-blue-500/20 text-blue-400' },
  high: { label: 'High', color: 'bg-orange-500/20 text-orange-400' },
  urgent: { label: 'Urgent', color: 'bg-red-500/20 text-red-400' }
};

const SOURCE_OPTIONS = [
  { value: 'contact_form', label: 'Contact Form' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'other', label: 'Other' }
];

export default function AdminTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [staff, setStaff] = useState([]);
  
  // Customer search state
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    customer_id: '',
    customer_email: '',
    customer_name: '',
    source: 'email',
    priority: 'medium',
    assigned_to: '',
    visibility: 'public'
  });
  
  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchTickets();
    fetchStats();
    fetchStaff();
  }, [statusFilter, priorityFilter]);

  const fetchTickets = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (priorityFilter) params.append('priority', priorityFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const res = await axios.get(`${API}/admin/tickets?${params}`, authHeaders);
      setTickets(res.data.tickets || []);
    } catch (err) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/tickets/stats`, authHeaders);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load stats');
    }
  };

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${API}/admin/staff`, authHeaders);
      setStaff(res.data.staff || []);
    } catch (err) {
      console.error('Failed to load staff');
    }
  };

  const searchCustomers = async (query) => {
    if (query.length < 2) {
      setCustomerResults([]);
      return;
    }
    
    setSearchingCustomers(true);
    try {
      const res = await axios.get(`${API}/admin/tickets/search-customers?q=${encodeURIComponent(query)}`, authHeaders);
      setCustomerResults(res.data.customers || []);
    } catch (err) {
      console.error('Failed to search customers');
      setCustomerResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  };

  const handleCustomerSearchChange = (value) => {
    setCustomerSearch(value);
    setSelectedCustomer(null);
    setNewTicket(prev => ({ ...prev, customer_id: '', customer_email: value, customer_name: '' }));
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      searchCustomers(value);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.email);
    setCustomerResults([]);
    setNewTicket(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_email: customer.email,
      customer_name: customer.name || customer.company_name || customer.email.split('@')[0]
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTickets();
  };

  const createTicket = async () => {
    if (!newTicket.subject.trim()) {
      toast.error('Subject is required');
      return;
    }
    
    setCreating(true);
    try {
      const payload = { ...newTicket };
      if (!payload.assigned_to) delete payload.assigned_to;
      if (!payload.customer_id) delete payload.customer_id;
      
      const res = await axios.post(`${API}/admin/tickets`, payload, authHeaders);
      toast.success(`Ticket ${res.data.ticket_number} created`);
      setShowCreateDialog(false);
      setNewTicket({
        subject: '',
        description: '',
        customer_id: '',
        customer_email: '',
        customer_name: '',
        source: 'email',
        priority: 'medium',
        assigned_to: ''
      });
      setCustomerSearch('');
      setSelectedCustomer(null);
      setCustomerResults([]);
      fetchTickets();
      fetchStats();
    } catch (err) {
      toast.error('Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="tickets-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <MessageSquare className="w-8 h-8 text-[#78BE20]" />
              Tickets & Communications
            </h1>
            <p className="text-muted-foreground">Manage customer inquiries and communications</p>
          </div>
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-[#78BE20] hover:bg-[#6aab1c]"
            data-testid="create-ticket-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Ticket
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{stats.total || 0}</div>
              <div className="text-xs text-muted-foreground">Total</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/20">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-400">{stats.open || 0}</div>
              <div className="text-xs text-blue-400/70">Open</div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-500/10 border-yellow-500/20">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-yellow-400">{stats.in_progress || 0}</div>
              <div className="text-xs text-yellow-400/70">In Progress</div>
            </CardContent>
          </Card>
          <Card className="bg-purple-500/10 border-purple-500/20">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-purple-400">{stats.waiting || 0}</div>
              <div className="text-xs text-purple-400/70">Waiting</div>
            </CardContent>
          </Card>
          <Card className="bg-zinc-500/10 border-zinc-500/20">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-zinc-400">{stats.closed || 0}</div>
              <div className="text-xs text-zinc-400/70">Closed</div>
            </CardContent>
          </Card>
          <Card className="bg-red-500/10 border-red-500/20">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-400">{stats.urgent || 0}</div>
              <div className="text-xs text-red-400/70">Urgent</div>
            </CardContent>
          </Card>
          <Card className="bg-orange-500/10 border-orange-500/20">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-orange-400">{stats.unassigned || 0}</div>
              <div className="text-xs text-orange-400/70">Unassigned</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tickets..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-zinc-800 border-zinc-700"
                    data-testid="ticket-search"
                  />
                </div>
              </form>
              
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[150px] bg-zinc-800 border-zinc-700" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="waiting">Waiting</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={priorityFilter} onValueChange={(v) => setPriorityFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[150px] bg-zinc-800 border-zinc-700" data-testid="priority-filter">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" onClick={fetchTickets} data-testid="refresh-tickets">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tickets List */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#78BE20]" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No tickets found</p>
                <Button 
                  variant="link" 
                  onClick={() => setShowCreateDialog(true)}
                  className="text-[#78BE20]"
                >
                  Create your first ticket
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-zinc-800">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="p-4 hover:bg-zinc-800/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/tickets/${ticket.id}`)}
                    data-testid={`ticket-row-${ticket.ticket_number}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-mono text-[#78BE20]">{ticket.ticket_number}</span>
                          <Badge variant="outline" className={STATUS_CONFIG[ticket.status]?.color}>
                            {STATUS_CONFIG[ticket.status]?.label}
                          </Badge>
                          <Badge variant="outline" className={PRIORITY_CONFIG[ticket.priority]?.color}>
                            {PRIORITY_CONFIG[ticket.priority]?.label}
                          </Badge>
                          {ticket.visibility === 'private' && (
                            <Badge variant="outline" className="bg-zinc-600/20 text-zinc-400 border-zinc-500/50 text-[10px]">
                              Private
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-medium truncate">{ticket.subject}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {ticket.customer_name || ticket.customer_email || 'Unknown'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(ticket.last_activity_at)}
                          </span>
                          {ticket.assigned_to && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              Assigned: {ticket.assigned_to}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        {ticket.linked_records?.estimates?.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <FileText className="w-3 h-3 mr-1" />
                            {ticket.linked_records.estimates.length}
                          </Badge>
                        )}
                        {ticket.linked_records?.invoices?.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <FileText className="w-3 h-3 mr-1" />
                            {ticket.linked_records.invoices.length}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Ticket Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Ticket</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Brief description of the inquiry"
                  className="bg-zinc-800 border-zinc-700"
                  data-testid="new-ticket-subject"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="customer_search">Customer (search by email or name)</Label>
                  <div className="relative">
                    <Input
                      id="customer_search"
                      value={customerSearch}
                      onChange={(e) => handleCustomerSearchChange(e.target.value)}
                      placeholder="Type to search existing customers..."
                      className="bg-zinc-800 border-zinc-700"
                      data-testid="customer-search"
                    />
                    {searchingCustomers && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                    )}
                    {customerResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-md shadow-lg max-h-48 overflow-auto">
                        {customerResults.map((customer) => (
                          <div
                            key={customer.id}
                            className="px-3 py-2 hover:bg-zinc-700 cursor-pointer flex items-center justify-between"
                            onClick={() => selectCustomer(customer)}
                          >
                            <div>
                              <div className="font-medium">{customer.name || customer.email.split('@')[0]}</div>
                              <div className="text-xs text-muted-foreground">{customer.email}</div>
                            </div>
                            {customer.company_name && (
                              <Badge variant="outline" className="text-xs">{customer.company_name}</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedCustomer && (
                    <div className="flex items-center gap-2 p-2 bg-[#78BE20]/10 border border-[#78BE20]/20 rounded-md">
                      <CheckCircle className="w-4 h-4 text-[#78BE20]" />
                      <span className="text-sm">Linked to: <strong>{selectedCustomer.name || selectedCustomer.email}</strong></span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 ml-auto text-xs"
                        onClick={() => {
                          setSelectedCustomer(null);
                          setCustomerSearch('');
                          setNewTicket(prev => ({ ...prev, customer_id: '', customer_email: '', customer_name: '' }));
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                  {!selectedCustomer && customerSearch && customerSearch.includes('@') && customerResults.length === 0 && !searchingCustomers && (
                    <div className="text-xs text-yellow-500">
                      No existing customer found. A new contact will be created.
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer_name">Display Name</Label>
                  <Input
                    id="customer_name"
                    value={newTicket.customer_name}
                    onChange={(e) => setNewTicket({ ...newTicket, customer_name: e.target.value })}
                    placeholder="John Doe"
                    className="bg-zinc-800 border-zinc-700"
                    disabled={!!selectedCustomer}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer_email">Email</Label>
                  <Input
                    id="customer_email"
                    type="email"
                    value={newTicket.customer_email}
                    onChange={(e) => setNewTicket({ ...newTicket, customer_email: e.target.value })}
                    placeholder="john@example.com"
                    className="bg-zinc-800 border-zinc-700"
                    disabled={!!selectedCustomer}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select 
                    value={newTicket.source} 
                    onValueChange={(v) => setNewTicket({ ...newTicket, source: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SOURCE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={newTicket.priority} 
                    onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Assign To</Label>
                  <Select 
                    value={newTicket.assigned_to || 'unassigned'} 
                    onValueChange={(v) => setNewTicket({ ...newTicket, assigned_to: v === 'unassigned' ? '' : v })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {staff.map((s) => (
                        <SelectItem key={s.id} value={s.email}>
                          {s.first_name} {s.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  placeholder="Initial notes or description..."
                  className="bg-zinc-800 border-zinc-700 min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label>Visibility</Label>
                <Select value={newTicket.visibility} onValueChange={(v) => setNewTicket({ ...newTicket, visibility: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="ticket-visibility-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public — Customer can see this ticket</SelectItem>
                    <SelectItem value="private">Private — Hidden from customer portal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={createTicket}
                disabled={creating}
                className="bg-[#78BE20] hover:bg-[#6aab1c]"
                data-testid="confirm-create-ticket"
              >
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Ticket
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
